/**
 * BookingCustomerCard — vista sintetica cliente CRM (mobile-first).
 *
 * Riceve il payload di /booking/customer-lookup o null. Mostra sempre i
 * badge disponibili, poi 4 metriche principali (visite, spesa, media, no
 * show) e — se popolate — sezioni tavolo/piatti/allergie/preferenze/note.
 */
import React from "react";
import { euros } from "@/lib/reservations";
import { CheckCircle2, AlertTriangle } from "lucide-react";

function Metric({ label, value }) {
  return (
    <div className="flex-1 min-w-0 text-center">
      <div className="text-[10px] uppercase tracking-widest text-ink-500 truncate">{label}</div>
      <div className="text-lg font-serif truncate">{value}</div>
    </div>
  );
}

export default function BookingCustomerCard({ card, loading }) {
  if (loading) {
    return (
      <div className="rounded-xl border border-sand-300 bg-white p-3 text-sm text-ink-500 animate-pulse"
           data-testid="booking-crm-card-loading">
        Ricerca cliente…
      </div>
    );
  }
  if (!card || !card.found) return null;

  const fullName = [card.name, card.surname].filter(Boolean).join(" ");
  return (
    <div className="rounded-xl border border-bronze-400/50 bg-white p-3 space-y-3"
         data-testid="booking-crm-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-bronze-700">Cliente CRM esistente</div>
          <div className="text-lg font-serif truncate">{fullName || "Cliente"}</div>
          {card.email && <div className="text-xs text-ink-500 truncate">{card.email}</div>}
        </div>
        <div className="flex-shrink-0">
          {card.profile_complete
            ? <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300">
                <CheckCircle2 className="w-3 h-3" /> Profilo completo
              </span>
            : <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">
                <AlertTriangle className="w-3 h-3" /> Profilo incompleto
              </span>}
        </div>
      </div>

      {card.badges?.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="booking-crm-badges">
          {card.badges.map((b) => (
            <span key={b.key}
                  className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 bg-sand-50 border border-sand-300 text-ink-900">
              <span>{b.icon}</span> {b.label}
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2 pt-2 border-t border-sand-200">
        <Metric label="Visite" value={card.visits ?? 0} />
        <Metric label="Ultima" value={card.last_visit || "—"} />
        <Metric label="Spesa" value={card.total_spent_cents ? euros(card.total_spent_cents) : "—"} />
        <Metric label="No show" value={card.no_shows ?? 0} />
      </div>

      {(card.favorite_table || card.favorite_dishes?.length || card.favorite_drinks?.length
        || card.allergies?.length || card.preferences?.length || card.crm_notes) && (
        <div className="pt-2 border-t border-sand-200 text-xs space-y-1">
          {card.favorite_table && (
            <div><b>Tavolo preferito:</b> {card.favorite_table}</div>
          )}
          {card.favorite_dishes?.length > 0 && (
            <div><b>Piatti preferiti:</b> {card.favorite_dishes.join(", ")}</div>
          )}
          {card.favorite_drinks?.length > 0 && (
            <div><b>Bevande preferite:</b> {card.favorite_drinks.join(", ")}</div>
          )}
          {card.allergies?.length > 0 && (
            <div><b>Allergie:</b> {card.allergies.join(", ")}</div>
          )}
          {card.preferences?.length > 0 && (
            <div><b>Preferenze:</b> {card.preferences.join(", ")}</div>
          )}
          {card.crm_notes && (
            <div className="text-ink-700 italic">📝 {card.crm_notes}</div>
          )}
        </div>
      )}
    </div>
  );
}
